import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoFGTSResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosFGTS = () => {
  const [saldoOriginal, setSaldoOriginal] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [resultado, setResultado] = useState<CalculoFGTSResult | null>(null);
  
  const { calcularFGTS, loading } = useCalculos();

  useCalculoPrefill("fgts", (v) => {
    setSaldoOriginal(v.saldoOriginal ?? "");
    setDataInicial(v.dataInicial ?? "");
    setDataFinal(v.dataFinal ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!saldoOriginal || !dataInicial || !dataFinal) {
      return;
    }

    try {
      const result = await calcularFGTS({
        saldoOriginal: parseFloat(saldoOriginal),
        dataInicial,
        dataFinal
      });
      setResultado(result);
      addToHistory({
        type: "fgts",
        label: "Correção FGTS",
        values: { saldoOriginal, dataInicial, dataFinal },
        summary: `Saldo ${formatCurrency(parseFloat(saldoOriginal))} • Diferença ${formatCurrency(result.diferenca)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Correção FGTS</CardTitle>
        <p className="text-sm text-muted-foreground">
          Correção oficial: TR + 3% a.a. vs. INPC/IPCA-E (para ações revisionais)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="saldo-original">Saldo Original FGTS</Label>
            <Input
              id="saldo-original"
              type="number"
              placeholder="5000.00"
              value={saldoOriginal}
              onChange={(e) => setSaldoOriginal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-inicial">Data Inicial</Label>
            <Input
              id="data-inicial"
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-final">Data Final</Label>
            <Input
              id="data-final"
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Correção FGTS"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Resultado da Correção:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Saldo Original:</strong> {formatCurrency(parseFloat(saldoOriginal))}</p>
                <p><strong>Saldo Corrigido (INPC):</strong> {formatCurrency(resultado.saldoCorrigido)}</p>
              </div>
              <div>
                <p className="text-green-600"><strong>Diferença a Receber:</strong> {formatCurrency(resultado.diferenca)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              * Diferença entre correção pelo INPC vs TR aplicada pela Caixa
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};